import { getFunctions, httpsCallable } from "firebase/functions";

import { firebaseApp } from "./firebase";

export type CentralGeneratedFormType =
  | "release_authorization"
  | "vital_statistics_worksheet"
  | "receipt_of_remains"
  | "cremation_authorization"
  | "funeral_goods_statement";

export type CentralGeneratedFormStatus =
  | "not_generated"
  | "mapping_review"
  | "generating"
  | "generated"
  | "approved"
  | "queued_for_signature"
  | "voided";

export type CentralGeneratedFormField = {
  key: string;
  label: string;
  missing: boolean;
  overridden: boolean;
  overrideReason: string;
  required: boolean;
  sourceLabel: "Case record" | "Staff supplied";
  sourceValue: string;
  value: string;
};

export type CentralGeneratedFormRecord = {
  approvedAt: string | null;
  available: boolean;
  category: "authorizations" | "death_certificates" | "contracts";
  currentVersion: number;
  documentId: string | null;
  draftDirty: boolean;
  fields: CentralGeneratedFormField[];
  formType: CentralGeneratedFormType;
  missingFields: string[];
  ready: boolean;
  required: boolean;
  signatureStatus: "stored" | null;
  signedDocumentId: string | null;
  status: CentralGeneratedFormStatus;
  templateChecksum: string;
  templateId: string;
  templateVersion: string;
  title: string;
  triggerExplanation: string;
  updatedAt: string | null;
};

export type CentralGeneratedFormVersion = {
  approvedAt: string | null;
  createdAt: string | null;
  documentId: string;
  formType: CentralGeneratedFormType;
  generatedAt: string | null;
  mappedValues: Record<string, string>;
  pdfSha256?: string;
  queuedAt: string | null;
  signatureStatus?: "stored";
  signedDocumentId?: string;
  sizeBytes?: number;
  status: Exclude<CentralGeneratedFormStatus, "not_generated" | "mapping_review">;
  templateChecksum: string;
  templateId: string;
  templateVersion: string;
  version: number;
  voidedAt: string | null;
};

const functions = getFunctions(firebaseApp, "us-east1");

export async function listCentralGeneratedForms(caseId: string) {
  const callable = httpsCallable<
    { caseId: string },
    {
      case: { disposition: "burial" | "cremation" | "undecided"; id: string; lovedOneName: string };
      forms: CentralGeneratedFormRecord[];
    }
  >(functions, "listCentralGeneratedForms");
  return (await callable({ caseId })).data;
}

export async function updateCentralGeneratedFormMappings(input: {
  caseId: string;
  formType: CentralGeneratedFormType;
  mappings: Record<string, string>;
  overrideReasons: Record<string, string>;
}) {
  const callable = httpsCallable<typeof input, { formType: CentralGeneratedFormType }>(
    functions,
    "updateCentralGeneratedFormMappings",
  );
  return (await callable(input)).data;
}

export async function generateCentralForm(
  caseId: string,
  formType: CentralGeneratedFormType,
  requestId = generationRequestId(),
) {
  const callable = httpsCallable<
    { caseId: string; formType: CentralGeneratedFormType; requestId: string },
    { duplicate: boolean; documentId?: string; form?: { documentId: string; version: number }; version?: number }
  >(functions, "generateCentralForm");
  return (await callable({ caseId, formType, requestId })).data;
}

export async function listCentralGeneratedFormVersions(
  caseId: string,
  formType: CentralGeneratedFormType,
) {
  const callable = httpsCallable<
    { caseId: string; formType: CentralGeneratedFormType },
    { versions: CentralGeneratedFormVersion[] }
  >(functions, "listCentralGeneratedFormVersions");
  return (await callable({ caseId, formType })).data.versions;
}

export async function approveCentralGeneratedForm(
  caseId: string,
  formType: CentralGeneratedFormType,
  version: number,
) {
  const callable = httpsCallable<
    { caseId: string; formType: CentralGeneratedFormType; version: number },
    { formType: CentralGeneratedFormType; status: "approved"; version: number }
  >(functions, "approveCentralGeneratedForm");
  return (await callable({ caseId, formType, version })).data;
}

export async function voidCentralGeneratedForm(
  caseId: string,
  formType: CentralGeneratedFormType,
  version: number,
) {
  const callable = httpsCallable<
    { caseId: string; formType: CentralGeneratedFormType; version: number },
    { formType: CentralGeneratedFormType; status: "voided"; version: number }
  >(functions, "voidCentralGeneratedForm");
  return (await callable({ caseId, formType, version })).data;
}

export async function queueCentralGeneratedFormForSignature(
  caseId: string,
  formType: CentralGeneratedFormType,
  version: number,
) {
  const callable = httpsCallable<
    { caseId: string; formType: CentralGeneratedFormType; version: number },
    { created: boolean; handoffId: string; status: "queued" }
  >(functions, "queueCentralGeneratedFormForSignature");
  return (await callable({ caseId, formType, version })).data;
}

export function getCentralGeneratedFormErrorMessage(error: unknown) {
  const code = typeof error === "object" && error && "code" in error ? String(error.code) : "";
  const message = typeof error === "object" && error && "message" in error ? String(error.message) : "";
  if (code.includes("permission-denied") || code.includes("unauthenticated")) {
    return "Your Central account cannot complete this form action. Sign in again or contact an administrator.";
  }
  if (code.includes("failed-precondition") || code.includes("invalid-argument")) {
    return message.replace(/^FirebaseError:\s*/i, "") || "Review the required mapped fields and form status, then try again.";
  }
  if (code.includes("not-found")) return "The selected case or generated form no longer exists. Refresh this page.";
  if (code.includes("aborted")) return "This form changed while the action was running. Refresh and try again.";
  return "Central couldn’t complete this generated form operation. Try again.";
}

function generationRequestId() {
  if (typeof globalThis.crypto?.randomUUID === "function") return globalThis.crypto.randomUUID();
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
}
